import { ethers, Contract } from 'ethers';

export class PowerfulERC20Client {
    private contract: Contract;

    constructor(
        contractAddress: string,
        runner: ethers.ContractRunner
    ) {
        const abi = [
            // ERC20 Standard
            "function name() public view returns (string)",
            "function symbol() public view returns (string)",
            "function decimals() public view returns (uint8)",
            "function totalSupply() public view returns (uint256)",
            "function balanceOf(address account) public view returns (uint256)",
            "function transfer(address recipient, uint256 amount) public returns (bool)",
            "function allowance(address owner, address spender) public view returns (uint256)",
            "function approve(address spender, uint256 amount) public returns (bool)",
            "function transferFrom(address sender, address recipient, uint256 amount) public returns (bool)",
            
            // ERC20 Extensions
            "function increaseAllowance(address spender, uint256 addedValue) public returns (bool)",
            "function decreaseAllowance(address spender, uint256 subtractedValue) public returns (bool)",
            "function cap() public view returns (uint256)",
            "function mintingFinished() external view returns (bool)",
            
            // Mintable
            "function mint(address account, uint256 amount) external",
            "function finishMinting() external",
            
            // Burnable
            "function burn(uint256 amount) public",
            "function burnFrom(address account, uint256 amount) public",
            
            // ERC1363
            "function transferAndCall(address recipient, uint256 amount) public returns (bool)",
            "function transferAndCall(address recipient, uint256 amount, bytes calldata data) public returns (bool)",
            "function transferFromAndCall(address sender, address recipient, uint256 amount) public returns (bool)",
            "function transferFromAndCall(address sender, address recipient, uint256 amount, bytes calldata data) public returns (bool)",
            "function approveAndCall(address spender, uint256 amount) public returns (bool)",
            "function approveAndCall(address spender, uint256 amount, bytes calldata data) public returns (bool)",
            
            // Roles
            "function hasRole(bytes32 role, address account) public view returns (bool)",
            "function getRoleAdmin(bytes32 role) public view returns (bytes32)",
            "function grantRole(bytes32 role, address account) public",
            "function revokeRole(bytes32 role, address account) public",
            "function renounceRole(bytes32 role, address account) public",
            
            // TokenRecover
            "function recoverERC20(address tokenAddress, uint256 tokenAmount) public",
            
            // Events
            "event Transfer(address indexed from, address indexed to, uint256 value)",
            "event Approval(address indexed owner, address indexed spender, uint256 value)",
            "event RoleGranted(bytes32 indexed role, address indexed account, address indexed sender)",
            "event RoleRevoked(bytes32 indexed role, address indexed account, address indexed sender)",
            "event MintFinished()"
        ];
        
        this.contract = new ethers.Contract(contractAddress, abi, runner);
    }

    // ============ ERC20 Standard Methods ============

    async getName(): Promise<string> {
        return this.contract.name();
    }

    async getSymbol(): Promise<string> {
        return this.contract.symbol();
    }

    async getDecimals(): Promise<number> {
        return this.contract.decimals();
    }

    async getTotalSupply(): Promise<bigint> {
        return this.contract.totalSupply();
    }

    async getBalanceOf(account: string): Promise<bigint> {
        return this.contract.balanceOf(account);
    }

    async transfer(recipient: string, amount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.transfer(recipient, amount);
    }

    async getAllowance(owner: string, spender: string): Promise<bigint> {
        return this.contract.allowance(owner, spender);
    }

    async approve(spender: string, amount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.approve(spender, amount);
    }

    async transferFrom(sender: string, recipient: string, amount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.transferFrom(sender, recipient, amount);
    }

    // ============ ERC20 Extension Methods ============

    async increaseAllowance(spender: string, addedValue: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.increaseAllowance(spender, addedValue);
    }

    async decreaseAllowance(spender: string, subtractedValue: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.decreaseAllowance(spender, subtractedValue);
    }

    async getCap(): Promise<bigint> {
        return this.contract.cap();
    }

    async isMintingFinished(): Promise<boolean> {
        return this.contract.mintingFinished();
    }

    // ============ Mintable Methods ============

    async mint(account: string, amount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.mint(account, amount);
    }

    async finishMinting(): Promise<ethers.ContractTransactionResponse> {
        return this.contract.finishMinting();
    }

    // ============ Burnable Methods ============

    async burn(amount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.burn(amount);
    }

    async burnFrom(account: string, amount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.burnFrom(account, amount);
    }

    // ============ ERC1363 Methods ============

    async transferAndCall(recipient: string, amount: bigint, data?: string): Promise<ethers.ContractTransactionResponse> {
        if (data) {
            return this.contract.transferAndCall(recipient, amount, data);
        }
        return this.contract.transferAndCall(recipient, amount);
    }

    async transferFromAndCall(
        sender: string, 
        recipient: string, 
        amount: bigint, 
        data?: string
    ): Promise<ethers.ContractTransactionResponse> {
        if (data) {
            return this.contract.transferFromAndCall(sender, recipient, amount, data);
        }
        return this.contract.transferFromAndCall(sender, recipient, amount);
    }

    async approveAndCall(spender: string, amount: bigint, data?: string): Promise<ethers.ContractTransactionResponse> {
        if (data) {
            return this.contract.approveAndCall(spender, amount, data);
        }
        return this.contract.approveAndCall(spender, amount);
    }

    // ============ Role Methods ============

    async hasRole(role: string, account: string): Promise<boolean> {
        return this.contract.hasRole(role, account);
    }

    async getRoleAdmin(role: string): Promise<string> {
        return this.contract.getRoleAdmin(role);
    }

    async grantRole(role: string, account: string): Promise<ethers.ContractTransactionResponse> {
        return this.contract.grantRole(role, account);
    }

    async revokeRole(role: string, account: string): Promise<ethers.ContractTransactionResponse> {
        return this.contract.revokeRole(role, account);
    }

    async renounceRole(role: string, account: string): Promise<ethers.ContractTransactionResponse> {
        return this.contract.renounceRole(role, account);
    }

    // ============ TokenRecover Methods ============

    async recoverERC20(tokenAddress: string, tokenAmount: bigint): Promise<ethers.ContractTransactionResponse> {
        return this.contract.recoverERC20(tokenAddress, tokenAmount);
    }

    // ============ Event Listeners ============

    onTransfer(
    listener: (from: string, to: string, value: bigint, event: ethers.EventLog) => void
): ethers.Contract {
    this.contract.on("Transfer", listener);
    return this.contract;
}

    onApproval(
        listener: (owner: string, spender: string, value: bigint, event: ethers.EventLog) => void
    ): ethers.Contract {
        this.contract.on("Approval", listener);
        return this.contract;
    }

    onRoleGranted(
        listener: (role: string, account: string, sender: string, event: ethers.EventLog) => void
    ): ethers.Contract {
        this.contract.on("RoleGranted", listener);
        return this.contract;
    }

    onRoleRevoked(
        listener: (role: string, account: string, sender: string, event: ethers.EventLog) => void
    ): ethers.Contract {
        this.contract.on("RoleRevoked", listener);
        return this.contract; 
    }

    onMintFinished(
        listener: () => void
    ): ethers.Contract {
        this.contract.on("MintFinished", listener);
        return this.contract;
    }

    // ============ Utility Methods ============

    // Helper to format amounts according to token decimals
    async parseTokenAmount(amount: string): Promise<bigint> {
        const decimals = await this.getDecimals();
        return ethers.parseUnits(amount, decimals);
    }

    // Helper to format amounts to human-readable strings
    async formatTokenAmount(amount: bigint): Promise<string> {
        const decimals = await this.getDecimals();
        return ethers.formatUnits(amount, decimals);
    }
}